/* eslint-disable max-len */
import React, { useCallback, useMemo, useState } from 'react';
import {
  ContextMenuItemType,
  useApiClient,
  useFetchInfiniteES,
} from '@marageti/z4-lib';
import { SearchRequest, SearchResult } from '@marageti/z4-sdk/lib/entity';
import {
  ChecklistItem,
  TripRequest,
  TripRequestStatus,
} from '@marageti/z4-sdk/lib/travel';
import { useParams, useNavigate } from 'react-router-dom';
import { useConfirm } from 'material-ui-confirm';
import { useSnackbar } from 'notistack';
import MyQueueView from './my-queue-view';
import {
  getSearch,
  ListName,
  SortDirection,
  TravelerTag,
  useBucketCounts,
  useAssignedConversationsInfinite,
  InboxName,
} from './my-queue-utils';
import { useAuthStore } from '../../../store';
import { titleCase } from '../../../utils/helpers';
import useRemindersSearch from '../../../hooks/use-reminders-search';
import useReminderActions from '../../../hooks/use-reminder-actions';
import ReminderEditModal from '../../../components/modals/reminder-edit-modal';
import ReminderRescheduleModal from '../../../components/modals/reminder-reschedule-modal';

const DEFAULT_LIST = 'Traveler Inbox';

const formatName = (name: string) => {
  switch (name) {
    case DEFAULT_LIST:
      return DEFAULT_LIST;
    default:
      return titleCase(name.replace('-', ' ')) as ListName;
  }
};

const isInboxType = (listName: ListName): listName is InboxName => listName === 'Traveler Inbox' || listName === 'Partner Inbox';
const isRemindersType = (listName: ListName): boolean => listName === 'Reminders';

const MyQueueContainer = () => {
  const apiClient = useApiClient();
  const { selectedList } = useParams();
  const formattedSelectedList = formatName(
    (selectedList as ListName) || DEFAULT_LIST,
  );

  const { enqueueSnackbar } = useSnackbar();
  const confirm = useConfirm();

  const navigate = useNavigate();
  const { loggedInTeamMember } = useAuthStore();
  const [searchRequest, setSearchRequest] = useState<SearchRequest>(
    getSearch(formattedSelectedList as ListName, loggedInTeamMember?.id!),
  );
  const [selectedTag, setSelectedTag] = useState<TravelerTag>('All');
  const [sortDirection, setSortDirection] = useState<SortDirection>('asc');

  const isInboxSection = isInboxType(formattedSelectedList as ListName);
  const isRemindersSection = isRemindersType(formattedSelectedList as ListName);

  const skipTripRequestsFetch = isInboxSection || isRemindersSection;
  const {
    data,
    error,
    hasMore,
    isLoading: isLoadingTripRequests,
    loadMore,
    mutate,
  } = useFetchInfiniteES<TripRequest>(
    skipTripRequestsFetch
      ? () => Promise.resolve({
        hits: [],
        totalHits: 0,
      } as SearchResult<TripRequest>)
      : apiClient.tripRequestsClient.postTripRequestSearches,
    searchRequest,
    {
      revalidateOnFocus: !skipTripRequestsFetch,
    },
  );

  const {
    flattenedHits: inboxItems,
    error: inboxError,
    hasMore: inboxHasMore,
    isLoading: inboxIsLoading,
    loadMore: loadMoreInbox,
    mutate: mutateInboxList,
  } = useAssignedConversationsInfinite(
    isInboxSection ? (formattedSelectedList as InboxName) : 'Traveler Inbox',
    selectedTag,
    sortDirection,
    false,
    isInboxSection,
  );

  // Bucket counts (moved up to use in combined refetch)
  const { data: bucketCounts, mutate: mutateBucketCounts } = useBucketCounts(
    loggedInTeamMember?.id!,
  );

  // Reminders section hooks
  const {
    reminders,
    totalHits: remindersTotalHits,
    isLoading: isLoadingReminders,
    hasMore: hasMoreReminders,
    loadMore: loadMoreReminders,
    refetch: refetchReminders,
  } = useRemindersSearch({
    assignedTo: loggedInTeamMember?.id || '',
    statusFilter: 'pending',
  });

  // Combined refetch that updates both reminders list and sidebar counts
  const handleRefetchReminders = useCallback(() => {
    refetchReminders();
    mutateBucketCounts();
  }, [refetchReminders, mutateBucketCounts]);

  const {
    edit: editReminder,
    duplicate: duplicateReminder,
    reschedule: rescheduleReminder,
    toggleComplete: toggleCompleteReminder,
    dismiss: dismissReminder,
    reschedulingId,
    togglingCompleteId,
    editModalProps,
    rescheduleModalProps,
    assignees,
  } = useReminderActions({ refetch: handleRefetchReminders });

  const onChangeFilter = (name: ListName) => {
    const route = name === DEFAULT_LIST ? '' : `/${name.toLowerCase().replace(' ', '-')}`;
    navigate(`/trip-requests/my-queue${route}`);
    setSearchRequest(getSearch(name, loggedInTeamMember?.id!));
    setSelectedTag('All');
    setSortDirection('asc');
  };

  const onTravelerTagChange = (tag: TravelerTag) => {
    setSelectedTag(tag);
  };

  const onSortDirectionChange = (direction: SortDirection) => {
    setSortDirection(direction);
  };

  const ContextActions = (
    name: ListName,
    tripRequest: TripRequest,
  ): ContextMenuItemType[] | undefined => {
    const { id: tripRequestId, status, statusInfo } = tripRequest;
    switch (name) {
      case 'In Progress':
      case 'Match Declined':
      case 'Postponed':
        return [
          {
            label: 'Dismiss From Queue',
            action: async () => {
              try {
                await confirm({
                  title: 'Dismiss from Queue',
                  description: (
                    <>
                      Completing this action removes the request from your
                      assigned queue, indicating that you no longer take any
                      action on it.
                      <br />
                      <br />
                      If you are currently following or have postponed this
                      request, those flags will be removed. New messages will
                      still display notifications, and you can also find it in
                      the All queue.
                    </>
                  ),
                  confirmationText: 'Continue',
                });
                if (tripRequestId && tripRequest) {
                  if (
                    status === TripRequestStatus.PostponedStatus
                    && statusInfo.lastStatus
                  ) {
                    await apiClient.tripRequestsClient.putTripRequestStatus(
                      tripRequestId!,
                      {
                        id: tripRequestId,
                        status: statusInfo.lastStatus,
                      },
                    );
                  }
                  await apiClient.tripRequestsClient.deleteTripRequestChecklistItems(
                    tripRequestId!,
                    {
                      params: {
                        item: [
                          ChecklistItem.NeedsMatching,
                          ChecklistItem.NeedsScreening,
                        ],
                      },
                    },
                  );
                }
                enqueueSnackbar({
                  message: 'Trip request dismissed.',
                  variant: 'success',
                });
                mutate();
                mutateBucketCounts();
              } catch (e) {
                console.warn(e);
              }
            },
          },
        ];
      case 'Following':
        return [
          {
            label: 'Unfollow',
            action: async () => {
              await apiClient.tripRequestsClient.deleteTripRequestFollower(
                tripRequestId,
              );
              enqueueSnackbar({
                message: 'Unfollowed trip request.',
                variant: 'success',
              });
              mutate();
              mutateBucketCounts();
            },
          },
        ];
      default:
        return undefined;
    }
  };

  const items = useMemo(() => {
    if (isInboxSection) {
      return inboxItems;
    }

    return (
      data
        ?.flatMap(
          (searchResult: SearchResult<TripRequest>) => searchResult.hits,
        )
        .map((tripRequest) => ({
          tripRequest,
          contextMenuItems: ContextActions(
            formattedSelectedList as ListName,
            tripRequest,
          ),
        })) || []
    );
  }, [data, inboxItems, isInboxSection, formattedSelectedList]);

  const listError = isInboxSection ? inboxError : error;

  const onDismissConversation = async (tripRequestId: string) => {
    await apiClient.conversationsClient.putConversationDismiss(tripRequestId);
    enqueueSnackbar({
      message: 'Message dismissed.',
      variant: 'success',
    });
    if (isInboxSection) {
      await mutateInboxList();
    } else {
      mutate();
    }
    mutateBucketCounts();
  };

  const getListState = () => {
    if (isRemindersSection) {
      return {
        isLoading: isLoadingReminders,
        handleLoadMore: loadMoreReminders,
        hasMoreItems: hasMoreReminders,
      };
    }
    if (isInboxSection) {
      return {
        isLoading: inboxIsLoading,
        handleLoadMore: loadMoreInbox,
        hasMoreItems: inboxHasMore,
      };
    }
    return {
      isLoading: isLoadingTripRequests,
      handleLoadMore: loadMore,
      hasMoreItems: hasMore,
    };
  };
  const { isLoading, handleLoadMore, hasMoreItems } = getListState();

  return (
    <>
      <MyQueueView
        error={listError}
        hasMoreItems={hasMoreItems}
        isLoading={isLoading}
        items={items}
        loadMore={handleLoadMore}
        onChangeFilter={onChangeFilter}
        selectedList={formattedSelectedList}
        bucketCounts={bucketCounts}
        onDismissConversation={onDismissConversation}
        onTravelerTagChange={onTravelerTagChange}
        onSortDirectionChange={onSortDirectionChange}
        selectedTag={selectedTag}
        sortDirection={sortDirection}
        // Reminders props
        reminders={reminders}
        remindersTotalHits={remindersTotalHits}
        assignees={assignees}
        reschedulingTicketId={reschedulingId}
        togglingCompleteTicketId={togglingCompleteId}
        onEditTicket={editReminder}
        onDuplicateTicket={duplicateReminder}
        onRescheduleTicket={rescheduleReminder}
        onToggleComplete={toggleCompleteReminder}
        onDismissTicket={dismissReminder}
      />

      {/* Reminder Modals */}
      <ReminderEditModal
        open={editModalProps.open}
        onClose={editModalProps.onClose}
        onSave={editModalProps.onSave}
        ticket={editModalProps.ticket}
        defaultValues={editModalProps.defaultValues}
        assignees={editModalProps.assignees}
        isNew={editModalProps.isNew}
      />
      <ReminderRescheduleModal
        open={rescheduleModalProps.open}
        onClose={rescheduleModalProps.onClose}
        onSave={rescheduleModalProps.onSave}
        currentDueAt={rescheduleModalProps.currentDueAt}
      />
    </>
  );
};

export default MyQueueContainer;
